import { select, arc, pie, interpolate, range } from "d3";

function arcTween(prev, nextArc, outerR) {
  const arcFn = arc().innerRadius(0).outerRadius(outerR);
  const next = nextArc || { startAngle: 0, endAngle: 0 };
  const copy = { ...prev } || { startAngle: 0, endAngle: 0 };
  const interStart = interpolate(copy?.startAngle || 0, next.startAngle);
  const interEnd = interpolate(copy?.endAngle || 0, next.endAngle);
  return function intrpolateAngle(t) {
    const e = 0.0001;
    copy.startAngle = Math.max(e, interStart(t));
    copy.endAngle = Math.min(2 * Math.PI - e, interEnd(t));
    return arcFn(copy);
  };
}

function calcMiddleAngle(arcVal) {
  const { startAngle, endAngle } = arcVal;
  return startAngle + (endAngle - startAngle) / 2;
}

function tween(prev, nextArc, outerR) {
  const r = outerR + 10;
  const next = nextArc || { startAngle: 0, endAngle: 0 };
  const copy = { ...prev } || { startAngle: 0, endAngle: 0 };
  const interStart = interpolate(copy?.startAngle || 0, next.startAngle);
  const interEnd = interpolate(copy?.endAngle || 0, next.endAngle);
  return function intrpolateAngle(t) {
    copy.startAngle = interStart(t);
    copy.endAngle = interEnd(t);
    const middleAnge = calcMiddleAngle(copy);
    const xA = middleAnge - Math.PI / 2;
    const yA = middleAnge - Math.PI / 2;

    const x = r * Math.cos(xA) + 10 * Math.cos(xA);
    const y = r * Math.sin(yA);
    return `translate(${x}, ${y})`;
  };
}

function pieDiagram({ element, margin = 50, duration = 700 }) {
  const cl =
    "tw-text-white tw-stroke-current hover:tw-text-blueGray-300 tw-stroke-opacity-20";
  const svg = select(element);
  const arcDict = {};
  const arcDictTxt = {};
  let outerR = 0;
  let data = [];
  let items = [];

  function calcItems() {
    const ranges = range(items.length);
    const arcs = pie()
      .padAngle(0)
      .sort(null)
      .value((i) => items[i].value)(ranges);

    data = items.map((v, i) => ({ ...v, arc: arcs[i] }));
  }

  function calcOpacity(d, distRad = 0.5) {
    const currentAngle = calcMiddleAngle(d.arc);
    if (!d.isVisible) return 0;

    const hasConflict = (data || [])
      .filter((x) => x.key !== d.key)
      .filter((x) => x.isVisible && x.arc)
      .map((x) => calcMiddleAngle(x?.arc))
      .map((x) => currentAngle - x)
      .filter((x) => x >= 0)
      .find((x) => x <= distRad);

    return hasConflict ? 0 : 0.7;
  }

  function render() {
    const nodes = svg.selectAll("path").data(data, (d) => d.key);
    const txts = svg.selectAll("text").data(data, (d) => d.key);

    nodes.exit().remove();

    nodes
      .attr("fill", (d) => d.color)
      .attr(
        "data-tooltip",
        (d) => `${d.key}: ${d.label} (${d.value} of ${d.total})`
      )
      .attr("data-tooltip-follow-mouse", 1)
      .attr("data-tooltip-class", "plot-tooltip")
      .transition()
      .duration(duration)
      .attrTween("d", (d) => arcTween(arcDict[d.key], d.arc, outerR))
      .on("end", (d) => {
        arcDict[d.key] = d.arc;
      });

    nodes
      .enter()
      .append("path")
      .attr("stroke-width", 2)
      .attr("stroke-linejoin", "round")
      .attr("fill", (d) => d.color)
      .attr("class", cl)
      .attr(
        "data-tooltip",
        (d) => `${d.key}: ${d.label} (${d.value} of ${d.total})`
      )
      .attr("data-tooltip-follow-mouse", 1)
      .attr("data-tooltip-class", "plot-tooltip")
      .transition()
      .duration(duration)
      .attrTween("d", (d) => arcTween(arcDict[d.key], d.arc, outerR))
      .on("end", (d) => {
        arcDict[d.key] = d.arc;
      });

    txts.exit().remove();

    txts
      .attr("text-anchor", "middle")
      .attr("alignment-baseline", "middle")
      .attr("y", 0)
      .attr("dy", "0.1em")
      .transition()
      .duration(duration)
      .attrTween("transform", (d) => tween(arcDictTxt[d.key], d.arc, outerR))
      .attr("opacity", (d) => calcOpacity(d))
      .text((d) => d.label)
      .on("end", (d) => {
        arcDictTxt[d.key] = d.arc;
      });

    txts
      .enter()
      .append("text")
      .attr("text-anchor", "middle")
      .attr("class", "tw-text-2xs")
      .attr("x", 0)
      .attr("y", 0)
      .attr("dy", "0.1em")
      .text((d) => d.label)
      .attr("opacity", 0)
      .transition()
      .duration(duration)
      .attr("opacity", (d) => calcOpacity(d))
      .attrTween("transform", (d) => tween(arcDictTxt[d.key], d.arc, outerR))
      .on("end", (d) => {
        arcDictTxt[d.key] = d.arc;
      });
  }

  return {
    setDimensions: (width, height) => {
      outerR = (Math.min(width, height) - margin) / 2;
      svg.attr("height", height);
      svg.attr("width", width);
      svg.attr("viewBox", [-width / 2, -height / 2, width, height]);

      calcItems();
      render();
    },

    setData: (dataItems) => {
      items = dataItems;
      calcItems();
      render();
    },
  };
}

export default pieDiagram;
